import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { 
  BarChart3, TrendingUp, Users, Eye, Clock, DollarSign, 
  MessageCircle, Star, Heart, Video, Loader2, Calendar
} from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, subValue, color = 'electric' }) => {
  const colorClasses = {
    electric: 'text-electric bg-electric/20',
    green: 'text-green-400 bg-green-400/20',
    amber: 'text-amber-400 bg-amber-400/20',
    purple: 'text-purple-400 bg-purple-400/20',
    cyan: 'text-cyan-400 bg-cyan-400/20',
    red: 'text-red-400 bg-red-400/20',
  };

  return (
    <div className="bg-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-4">
      <div className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center mb-3`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="text-2xl font-bold text-foreground">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
      {subValue && <p className="text-xs text-muted-foreground/60 mt-1">{subValue}</p>}
    </div>
  );
};

const formatDuration = (seconds) => {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${mins}m`;
};

const AnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(30);
  const [overview, setOverview] = useState(null);
  const [popularContent, setPopularContent] = useState([]);
  const [engagement, setEngagement] = useState(null);
  const [adPerformance, setAdPerformance] = useState(null);
  const [revenueData, setRevenueData] = useState([]);

  useEffect(() => {
    loadAnalytics();
  }, [timeRange]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const [overviewRes, popularRes, engagementRes, adRes, revenueRes] = await Promise.all([
        api.get(`/analytics/overview?days=${timeRange}`),
        api.get(`/analytics/popular-content?days=${timeRange}&limit=5`),
        api.get(`/analytics/user-engagement?days=${timeRange}`),
        api.get(`/analytics/ad-performance?days=${timeRange}`),
        api.get(`/analytics/revenue-chart?days=${timeRange}`)
      ]);

      setOverview(overviewRes.data);
      setPopularContent(popularRes.data);
      setEngagement(engagementRes.data);
      setAdPerformance(adRes.data);
      setRevenueData(revenueRes.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 text-electric animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Time Range Selector */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
          <BarChart3 className="h-5 w-5 text-electric" />
          <span>Analytics Overview</span>
        </h3>
        <div className="flex items-center space-x-2">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(Number(e.target.value))}
            className="bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-electric"
            data-testid="time-range-select"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
      </div>

      {/* Overview Stats */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard 
            icon={Eye} 
            label="Total Views" 
            value={overview.total_views.toLocaleString()}
            color="electric"
          />
          <StatCard 
            icon={Clock} 
            label="Watch Time" 
            value={formatDuration(overview.total_watch_time)}
            subValue={`Avg: ${formatDuration(overview.avg_watch_time)}`}
            color="cyan"
          />
          <StatCard 
            icon={Users} 
            label="Unique Viewers" 
            value={overview.unique_viewers.toLocaleString()}
            color="purple"
          />
          <StatCard 
            icon={DollarSign} 
            label="Total Revenue" 
            value={`$${overview.total_revenue.toFixed(2)}`}
            subValue={`PPV: $${overview.ppv_revenue.toFixed(2)}`}
            color="green"
          />
        </div>
      )}

      {/* User Stats & Engagement */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* User Breakdown */}
        {overview && (
          <div className="bg-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-6">
            <h4 className="text-foreground font-semibold mb-4 flex items-center space-x-2">
              <Users className="h-5 w-5 text-electric" />
              <span>User Breakdown</span>
            </h4>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Premium Users</span>
                <span className="text-electric font-semibold">{overview.premium_users}</span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-2">
                <div 
                  className="bg-electric h-2 rounded-full"
                  style={{ 
                    width: `${overview.premium_users / (overview.premium_users + overview.free_users) * 100 || 0}%` 
                  }}
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Free Users</span>
                <span className="text-foreground font-semibold">{overview.free_users}</span>
              </div>
            </div>
          </div>
        )}

        {/* Engagement Metrics */}
        {engagement && (
          <div className="bg-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-6">
            <h4 className="text-foreground font-semibold mb-4 flex items-center space-x-2">
              <TrendingUp className="h-5 w-5 text-green-400" />
              <span>User Engagement</span>
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-white/5 rounded-xl">
                <Users className="h-5 w-5 text-electric mx-auto mb-1" />
                <p className="text-xl font-bold text-foreground">{engagement.new_signups}</p>
                <p className="text-xs text-muted-foreground">New Signups</p>
              </div>
              <div className="text-center p-3 bg-white/5 rounded-xl">
                <Eye className="h-5 w-5 text-cyan-400 mx-auto mb-1" />
                <p className="text-xl font-bold text-foreground">{engagement.active_users}</p>
                <p className="text-xs text-muted-foreground">Active Users</p>
              </div>
              <div className="text-center p-3 bg-white/5 rounded-xl">
                <MessageCircle className="h-5 w-5 text-purple-400 mx-auto mb-1" />
                <p className="text-xl font-bold text-foreground">{engagement.comments}</p>
                <p className="text-xs text-muted-foreground">Comments</p>
              </div>
              <div className="text-center p-3 bg-white/5 rounded-xl">
                <Heart className="h-5 w-5 text-red-400 mx-auto mb-1" />
                <p className="text-xl font-bold text-foreground">{engagement.favorites}</p>
                <p className="text-xs text-muted-foreground">Favorites</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Popular Content */}
      <div className="bg-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-6">
        <h4 className="text-foreground font-semibold mb-4 flex items-center space-x-2">
          <TrendingUp className="h-5 w-5 text-amber-400" />
          <span>Top Performing Content</span>
        </h4>
        {popularContent.length > 0 ? (
          <div className="space-y-3">
            {popularContent.map((content, index) => (
              <div 
                key={content.content_id} 
                className="flex items-center space-x-4 p-3 bg-white/5 rounded-xl hover:bg-white/10 transition-colors"
              >
                <span className="text-2xl font-bold text-muted-foreground w-8">
                  #{index + 1}
                </span>
                <img
                  src={content.thumbnail}
                  alt={content.title}
                  className="w-16 h-10 object-cover rounded-lg"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-foreground font-medium truncate">{content.title}</p>
                  <p className="text-xs text-muted-foreground capitalize">{content.content_type.replace('_', ' ')}</p>
                </div>
                <div className="flex items-center space-x-4 text-sm">
                  <div className="text-center">
                    <p className="text-foreground font-semibold">{content.views}</p>
                    <p className="text-xs text-muted-foreground">Views</p>
                  </div>
                  <div className="text-center">
                    <p className="text-foreground font-semibold">{content.avg_completion}%</p>
                    <p className="text-xs text-muted-foreground">Completion</p>
                  </div>
                  <div className="text-center">
                    <p className="text-amber-400 font-semibold flex items-center">
                      <Star className="h-3 w-3 mr-1 fill-amber-400" />
                      {content.rating.toFixed(1)}
                    </p>
                    <p className="text-xs text-muted-foreground">Rating</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-muted-foreground py-8">No content data available</p>
        )}
      </div>

      {/* Ad Performance */}
      {adPerformance && (
        <div className="bg-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-6">
          <h4 className="text-foreground font-semibold mb-4 flex items-center space-x-2">
            <Video className="h-5 w-5 text-purple-400" />
            <span>Ad Performance</span>
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="text-center p-4 bg-white/5 rounded-xl">
              <p className="text-2xl font-bold text-foreground">{adPerformance.total_impressions}</p>
              <p className="text-sm text-muted-foreground">Impressions</p>
            </div>
            <div className="text-center p-4 bg-white/5 rounded-xl">
              <p className="text-2xl font-bold text-green-400">{adPerformance.total_completions}</p>
              <p className="text-sm text-muted-foreground">Completions</p>
            </div>
            <div className="text-center p-4 bg-white/5 rounded-xl">
              <p className="text-2xl font-bold text-amber-400">{adPerformance.total_skips}</p>
              <p className="text-sm text-muted-foreground">Skips</p>
            </div>
            <div className="text-center p-4 bg-white/5 rounded-xl">
              <p className="text-2xl font-bold text-electric">{adPerformance.completion_rate}%</p>
              <p className="text-sm text-muted-foreground">Completion Rate</p>
            </div>
          </div>
          
          {/* Pre-roll vs Mid-roll breakdown */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-white/5 rounded-xl">
              <p className="text-sm text-muted-foreground mb-2">Pre-roll Ads</p>
              <div className="flex items-center justify-between">
                <span className="text-foreground">{adPerformance.preroll.impressions} impressions</span>
                <span className="text-green-400">{adPerformance.preroll.completions} completed</span>
              </div>
            </div>
            <div className="p-4 bg-white/5 rounded-xl">
              <p className="text-sm text-muted-foreground mb-2">Mid-roll Ads</p>
              <div className="flex items-center justify-between">
                <span className="text-foreground">{adPerformance.midroll.impressions} impressions</span>
                <span className="text-green-400">{adPerformance.midroll.completions} completed</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Revenue Chart */}
      {revenueData.length > 0 && (
        <div className="bg-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-6">
          <h4 className="text-foreground font-semibold mb-4 flex items-center space-x-2">
            <DollarSign className="h-5 w-5 text-green-400" />
            <span>Revenue Trend</span>
          </h4>
          <div className="h-48 flex items-end space-x-1">
            {revenueData.slice(-14).map((day, i) => {
              const maxRevenue = Math.max(...revenueData.map(d => d.total), 1);
              const height = (day.total / maxRevenue) * 100;
              return (
                <div 
                  key={day.date} 
                  className="flex-1 flex flex-col items-center group"
                >
                  <div className="relative w-full">
                    <div 
                      className="w-full bg-green-400/80 rounded-t transition-all hover:bg-green-400"
                      style={{ height: `${Math.max(height, 2)}px` }}
                    />
                    <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-background border border-white/20 rounded px-2 py-1 text-xs whitespace-nowrap z-10">
                      ${day.total.toFixed(2)}
                    </div>
                  </div>
                  {i % 2 === 0 && (
                    <span className="text-xs text-muted-foreground mt-2 rotate-45 origin-left">
                      {day.date.slice(5)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
